<template>
    <div class="form_flex_wrap loading_top_wrap">
        <div v-if="isLoading" class="loading_bg round_none transparent" style="z-index: 999;">
            <div class="loading_wrap">
                <div class="loading_obj">
                    <svg class="spinner" width="60px" height="60px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                        <circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>
                    </svg>
                </div>
            </div>
        </div>
        <div class="form_flex_title">
            <span class="title">{{ $t('reservation_settings.section_program_management.title') }}</span>
            <span class="info mgl_auto">{{ $t('reservation_settings.section_program_management.sub_title') }}</span>
        </div>
        <div class="form_flex_cont">
            <div class="table_top_wrap">
                <b>{{ $t('reservation_settings.section_program_management.label_program') }}</b>
                <button class="btn btn_s btn_blue_line mgl_auto" @click="createItem()">＋ {{ $t('common.add') }}</button>
            </div>
            <table class="table2_default">
                <colgroup>
                    <col width="220">
                    <col width="120">
                    <col>
                    <col width="140">
                    <col width="115">
                </colgroup>
                <thead>
                    <tr>
                        <th>{{ $t('reservation_settings.section_program_management.label_program_title') }}</th>
                        <th>{{ $t('reservation_settings.section_program_management.label_duration') }}</th>
                        <th>{{ $t('reservation_settings.section_program_management.label_program_description') }}</th>
                        <th>{{ $t('reservation_settings.section_program_management.label_change_order') }}</th>
                        <th>{{ $t('common.delete') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="(this.programs?.length ?? 0) == 0">
                        <td class="txt_center lgray_txt" colspan="5">{{ $t('reservation_settings.section_program_management.label_no_program') }}</td>
                    </tr>

                    <ReservationProgramItem v-for="(item, index) in programs" :key="index"
                        :item="item"
                        :index="index"
                        :programs="programs"
                        @updateOrder="updateOrder"
                        @removeItem="removeItem"
                        @updateItem="updateItem"/>
                </tbody>
            </table>
            <small class="red_txt mgt_10" v-if="activeErrorMessage != undefined">{{ activeErrorMessage }}</small>
        </div>
        <div class="btn_wrap">
            <span class="editor_info mgr_auto">{{ modifiedAt }} / {{ modifier }}</span>
            <button class="btn btn_l btn_blue mgl_auto" @click="saveReservationPrograms()">{{ $t('common.save') }}</button>
        </div>
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
const moment = require('@/util/MPMoment')

import { SpaceNavigationAction } from '../Enums/SpaceNavigationAction.js'
import ReservationProgramItem from '../SubViews/ReservationProgramItem.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { v2ReservationProgramListQueryOptions } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-find-query'
import { v2ReservationSettingFindOneQueryOptions } from '@/composables/queries/v2reservationsetting/use-v2-reservationsetting-find-one-query'
import { useV2ReservationProgramCreateMutation } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-create-mutation'
import { useV2ReservationProgramUpdateMutation } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-update-mutation'
import { useV2ReservationProgramRemoveMutation } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-remove-mutation'
import { useV2ReservationProgramSendPushMutation } from '@/composables/queries/v2reservationprogram/use-v2-reservationprogram-send-push-mutation'

export default {
    setup() {
        const queryClient = useQueryClient()
        const { mutateAsync: createProgram } = useV2ReservationProgramCreateMutation()
        const { mutateAsync: updateProgram } = useV2ReservationProgramUpdateMutation()
        const { mutateAsync: removeProgram } = useV2ReservationProgramRemoveMutation()
        const { mutateAsync: sendProgramPush } = useV2ReservationProgramSendPushMutation()
        return { queryClient, createProgram, updateProgram, removeProgram, sendProgramPush }
    },
    components: { ReservationProgramItem },
    props: {

    },
    data() {
        return {
            isLoading: false,
            isSaving: false,

            originalPrograms: undefined,
            programs: undefined,
            settingValue: undefined,
            itemsToBeDeleted: [],

            errorType: {
                emptyPrgramsError: '최소 1개 이상의 프로그램을 등록해주세요.',
                requiredDataError: this.$t('reservation_settings.section_program_management.error_title_and_duration_required'),
                timeSettingError: '소요시간은 5분 간격으로 설정할 수 있습니다.',
                duplicateError: '동일한 프로그램명으로 등록할 수 없습니다. 다른 명칭으로 설정해 주세요.'
            },
            activeErrorMessage: undefined
        }
    },
    watch: {
        originalPrograms: {
            immediate: true,
            deep: true,
            handler: function(newValue) {
                const filteredPrograms = deepCopy(newValue)?.filter(p => !p.isDefault)
                this.programs = filteredPrograms
            }
        }
    },
    computed: {
        modifiedAt: function() {
            if (this.settingValue?.programModifiedAt == undefined) {
                return '-'
            } else {
                return moment(this.settingValue?.programModifiedAt).toString(this.$t('common.date_format_YYYY_MM_DD_HH_mm'))
            }
        },
        modifier: function() {
            if (this.settingValue?.programModifierID == undefined) {
                return '-'
            } else {
                if (this.settingValue.programModifierID.type >= 3000) {
                    return this.$t('reservation_settings.section_program_management.label_modified_afterdoc')
                } else {
                    return `${this.settingValue.programModifierID.realName} ${this.$t('reservation_settings.section_program_management.label_modified')}`
                }
            }
        },
    },
    mounted() {
        this.requestFetchReservationProgram()
        this.reqeustFetchReservationSetting()
    },
    methods: {
        // 아이템 순서 변경
        updateOrder(action, index) {
            let program = this.programs.splice(index, 1)[0]
            switch(action) {
                case SpaceNavigationAction.FIRST:
                    this.programs.splice(0, 0, program)
                    break
                case SpaceNavigationAction.PREVIOUS:
                    this.programs.splice(index - 1, 0, program)
                    break
                case SpaceNavigationAction.NEXT:
                    this.programs.splice(index + 1, 0, program)
                    break
                case SpaceNavigationAction.LAST: 
                    this.programs.splice(this.programs.length, 0, program)
                    break
            }
        },
        // 아이템 추가
        createItem() {
            this.isShowEmptyPrgramsError = false
            this.programs.push({
                duration: 30
            })
        },
        // 아이템 변경
        updateItem(item, index) {
            this.programs.splice(index, 1, item)
            this.$forceUpdate()
        },
        // 아이템 삭제
        removeItem(index) {
            if (!this.programs[index]._id != undefined) {
                this.itemsToBeDeleted.push(this.programs[index]._id)
            }
            this.programs.splice(index, 1)
        },
        // 저장가능여부 체크
        checkPrograms() {
            this.activeErrorMessage = undefined

            if (this.programs.length == 0) {
                this.activeErrorMessage = this.errorType.emptyPrgramsError
                return false
            }
            let invalidProgramIndex = this.programs.findIndex(p => !p.title || p.duration == undefined || p.duration == 0)
            if (invalidProgramIndex != -1) {
                this.activeErrorMessage = this.errorType.requiredDataError
                return false
            }
            let invalidTimeIndex = this.programs.findIndex(p => p.duration % 5 != 0)
            if (invalidTimeIndex != -1) {
                this.activeErrorMessage = this.errorType.timeSettingError
                return false
            }

            let hasDuplicates = new Set(this.programs.map(p => p.title)).size != this.programs.length
            if (hasDuplicates) {
                this.activeErrorMessage = this.errorType.duplicateError
                return false
            }
            return true
        },
        // 프로그램 목록 저장
        async saveReservationPrograms() {
            try {
                if (this.isSaving || !this.checkPrograms()) { return }

                this.isLoading = true
                this.isSaving = true
                
                for (let i = 0; i < this.itemsToBeDeleted.length; i++) {
                    await this.requestRemoveReservationProgram(this.itemsToBeDeleted[i])
                }
                for (let i = 0; i < this.programs.length; i++) {
                    let data = this.programs[i]
                    data.sortNum = i
                    await this.requestCreateOrUpdateReservationPrograms(data)
                }

                this.requestFetchReservationProgram()
                this.requestUpdateCompletePush()

                this.setToast({
                    isShow: true,
                    text: this.$t('common.label_save_success')
                })

                this.reqeustFetchReservationSetting()

            } catch (err) {
                console.log('failed to save reservation programs' , err)
            } finally {
                this.isLoading = false
                this.isSaving = false
            }
        },
        // 프로그램 목록 조회
        async requestFetchReservationProgram() {
            try {
                this.isLoading = true

                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                this.originalPrograms = await this.queryClient.fetchQuery(v2ReservationProgramListQueryOptions(this.hospital.selectedHospital._id))
            } catch (err) {
                console.log('reservation program find error : ', err)
            } finally {
                this.isLoading = false
            }
        },
        // 프로그램 생성 / 수정
        async requestCreateOrUpdateReservationPrograms(program) {
            let data = program
            if (data._id == undefined || data._id == '') {
                data = {
                    hospitalID: this.hospital.selectedHospital._id,
                    title: data.title,
                    duration: data.duration,
                    description: data.description,
                    sortNum: data.sortNum
                }
                await this.createProgram(data)
            } else {
                await this.updateProgram(data)
            }
        },
        // 상태 삭제
        async requestRemoveReservationProgram(id) {
            if (id == undefined) { return }
            let data = { _id: id }
            await this.removeProgram(data)
        },
        // 변경완료 푸시
        requestUpdateCompletePush() {
            // 기존과 동일하게 fire-and-forget — 실패는 로그만
            this.sendProgramPush({ hospitalID: this.hospital.selectedHospital._id }).catch(() => console.log('push send fail'))
        },
        // 예약설정 조회
        async reqeustFetchReservationSetting() {
            try {
                this.isLoading = true
                let param = {
                    hospitalID: this.hospital.selectedHospital._id,
                }
                // 봉투 에러(code != 0)는 ApiError로 throw — 아래 catch에서 로그 (기존 동작 동일)
                this.settingValue = await this.queryClient.fetchQuery(v2ReservationSettingFindOneQueryOptions(param))
            } catch (err) {
                console.log('reservation setting find error from program management component ', err)
            } finally {
                this.isLoading = false
            }
        },
    }
}
</script>